import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Download, Scale, Search, Telescope } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Explain } from '@/components/Explain';
import { Timestamp } from '@/components/primitives';
import { cn } from '@/lib/cn';
import type { Bootstrap, LookupParams } from '@/lib/bootstrap';
import { parseClickhouseTime, type TimeZoneMode } from '@/lib/format';
import type { CoreTransactionRow, ReconciliationResult } from '@/types';
import { ApiError, fetchClients } from '@/features/bet-inspection/lib/api';
import { defaultRange, toClickhouseUtc } from '@/features/player-reports/lib/time';
import { FilterBar, type ReconFilters } from './components/FilterBar';
import { fetchReconciliation } from './lib/api';
import { coreToCsv, downloadCsv, reconFilename } from './lib/csv';

/** Rendered columns ARE the exported columns — what you see is what you get. */
const COLUMNS: (keyof CoreTransactionRow)[] = [
  'id',
  'dt',
  'action',
  'type',
  'game_uuid',
  'bet_id',
  'round_id',
  'player_id',
  'amount',
  'currency',
  'rollbacked',
  'rollback_id',
  'client_trx_id',
  'freeround_id',
  'session_id',
  'play_service_id',
];

/** Rendering thousands of rows freezes the tab; the export is never capped. */
const RENDER_CAP = 500;

/**
 * Reconciliation — the feed our PARTNERS settle their books against.
 *
 * An operator reconciles by pulling game-core's `/reconciliation` for a period
 * and matching it against their own records. This screen shows support exactly
 * what a given partner receives from that call: betlens signs it with the same
 * merchant_key the partner uses, so this IS their file, not an approximation.
 *
 * Nothing here is re-derived, re-ordered or enriched. When an operator says
 * "your reconciliation data is wrong", this is the file under discussion, and a
 * console that quietly reformatted it would be answering a different question.
 */
export function Reconciliation({
  boot,
  tz,
  onOpenBet,
}: {
  boot: Bootstrap;
  tz: TimeZoneMode;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  const [filters, setFilters] = useState<ReconFilters>(() => {
    const { from, to } = defaultRange();
    return { psid: '', from, to, gameUuid: '', betId: '', playerId: '' };
  });
  const [find, setFind] = useState('');

  const clients = useQuery({
    queryKey: ['clients', boot.api],
    queryFn: ({ signal }) => fetchClients({ api: boot.api!, token: boot.token, signal }),
    enabled: Boolean(boot.api),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const run = useMutation<ReconciliationResult, Error, ReconFilters>({
    mutationFn: (f) =>
      fetchReconciliation({
        api: boot.api!,
        token: boot.token,
        input: {
          play_service_id: f.psid,
          from: toClickhouseUtc(f.from),
          to: toClickhouseUtc(f.to),
          ...(f.gameUuid ? { game_uuid: f.gameUuid } : {}),
          ...(f.betId ? { bet_id: f.betId } : {}),
          ...(f.playerId ? { player_id: f.playerId } : {}),
        },
      }),
  });

  const result = run.data;
  const rows = result?.feed.data ?? [];

  // Exports are named for the project a human recognises, not the opaque psid.
  const clientName = useMemo(
    () =>
      (clients.data ?? []).find((c) => c.play_service_id === run.variables?.psid)?.client_name ||
      run.variables?.psid ||
      '',
    [clients.data, run.variables],
  );

  // Client-side find across the fetched feed. Deliberately does NOT re-query:
  // narrowing the request would change the file, and the file is the point.
  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      COLUMNS.some((c) => String(r[c] ?? '').toLowerCase().includes(needle)),
    );
  }, [rows, find]);

  return (
    <>
      <div className="sticky top-[41px] z-30 border-b border-hairline bg-bg/95 backdrop-blur">
        <FilterBar
          value={filters}
          onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
          onSearch={() => run.mutate(filters)}
          onReset={() => {
            const { from, to } = defaultRange();
            setFilters((f) => ({ psid: f.psid, from, to, gameUuid: '', betId: '', playerId: '' }));
            setFind('');
            run.reset();
          }}
          loading={run.isPending}
          clients={clients.data ?? []}
          clientsLoading={clients.isLoading}
          clientsError={clients.isError}
          api={boot.api!}
          token={boot.token}
          tz={tz}
        />
      </div>

      {run.isPending && <Scanning />}
      {run.isError && <Failure error={run.error} />}
      {!run.isPending && !run.isError && !result && <Idle />}

      {result && !run.isPending && (
        <main className="rise">
          <Summary
            result={result}
            clientName={clientName}
            find={find}
            onFind={setFind}
            shown={shown.length}
          />

          {result.feed.status === 'unavailable' || result.feed.status === 'skipped' ? (
            <Unavailable result={result} />
          ) : rows.length === 0 ? (
            <Empty />
          ) : (
            <Feed rows={shown} tz={tz} onOpenBet={onOpenBet} find={find} />
          )}
        </main>
      )}
    </>
  );
}

function Summary({
  result,
  clientName,
  find,
  onFind,
  shown,
}: {
  result: ReconciliationResult;
  clientName: string;
  find: string;
  onFind: (v: string) => void;
  shown: number;
}) {
  const rows = result.feed.data ?? [];
  const reachable = result.feed.status !== 'unavailable' && result.feed.status !== 'skipped';

  return (
    <>
      <section className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-hairline bg-surface px-4 py-2.5">
        <Cell label="transactions">
          <span className="tnum font-mono text-[13px] font-semibold text-text">
            {result.totals.transactions}
          </span>
        </Cell>
        <Cell label="bets">
          <span className="tnum font-mono text-[13px] text-text-dim">{result.totals.bets}</span>
        </Cell>
        <Cell label="window">
          <span className="tnum font-mono text-[11.5px] text-text-dim">
            {result.window.from} → {result.window.to}
          </span>
        </Cell>

        {reachable && rows.length > 0 && (
          <>
            <label className="relative ml-auto block w-[200px]">
              <Search
                size={12}
                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-text-faint"
              />
              <input
                value={find}
                onChange={(e) => onFind(e.target.value)}
                placeholder="find in feed"
                spellCheck={false}
                autoComplete="off"
                className={cn(
                  'w-full border border-hairline bg-surface-sunken py-1 pl-7 pr-2',
                  'font-mono text-[12px] text-text placeholder:text-text-faint',
                  'hover:border-hairline-strong focus:border-accent focus:outline-none',
                )}
              />
            </label>
            <button
              type="button"
              onClick={() =>
                downloadCsv(
                  reconFilename('partner-feed', clientName, result.window.from),
                  // Always the FULL feed, never the on-screen filter — an export
                  // of a search result is not the partner's file.
                  coreToCsv(rows),
                )
              }
              title="Export every row of the feed, regardless of the on-screen filter"
              className="inline-flex items-center gap-1.5 border border-accent/40 bg-accent/10 px-3 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider text-accent hover:bg-accent/20"
            >
              <Download size={12} /> Export CSV
            </button>
          </>
        )}
      </section>

      {find.trim() && (
        <p className="border-b border-hairline bg-surface-sunken px-4 py-1.5 text-[11.5px] text-text-dim">
          Showing {shown} of {rows.length} rows matching “{find.trim()}”. The export still contains
          the complete feed.
        </p>
      )}

      {result.truncated && (
        <p className="flex gap-2 border-b border-hairline bg-warn-bg px-4 py-2 text-[12px] leading-relaxed text-text">
          <AlertCircle size={13} className="mt-[2px] shrink-0 text-warn" />
          {/* game-core caps its inner query at 1000 bets and says nothing about
              it — inferred here so a partial file never reads as a complete one. */}
          <span>
            The feed returned its maximum of <strong>1000 bets</strong>. A partner pulling this same
            window would also receive a truncated file, with no warning of their own. Narrow the
            window.
          </span>
        </p>
      )}
    </>
  );
}

function Feed({
  rows,
  tz,
  find,
  onOpenBet,
}: {
  rows: CoreTransactionRow[];
  tz: TimeZoneMode;
  find: string;
  onOpenBet: (lookup: LookupParams) => void;
}) {
  return (
    <>
      <p className="border-b border-hairline bg-surface-sunken px-4 py-2 text-[11.5px] leading-relaxed text-text-dim">
        Byte for byte what the partner receives: betlens calls the same{' '}
        <span className="font-mono">/reconciliation</span> endpoint, signed with the same merchant
        key the operator uses.{' '}
        <span className="text-text-faint">
          Note that <span className="font-mono">amount</span> is a floating-point number in the feed
          — the trailing decimal precision our database holds is already gone, so{' '}
          <span className="font-mono">1.40</span> reaches the partner as{' '}
          <span className="font-mono">1.4</span>.
        </span>
      </p>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr className="border-b border-hairline bg-surface">
              {COLUMNS.map((col) => (
                <Th key={col} align={col === 'amount' ? 'right' : 'left'}>
                  {col}
                </Th>
              ))}
              <Th />
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, RENDER_CAP).map((row, i) => (
              <tr
                key={`${row.id}-${i}`}
                className="border-b border-hairline/60 hover:bg-surface-raised"
              >
                {COLUMNS.map((col) => (
                  <td
                    key={col}
                    className={cn(
                      'whitespace-nowrap px-3 py-1 font-mono text-[11.5px]',
                      col === 'amount' ? 'tnum text-right text-text' : 'text-text-dim',
                    )}
                  >
                    {col === 'dt' ? <FeedTime value={row.dt} tz={tz} /> : String(row[col] ?? '')}
                  </td>
                ))}
                <td className="px-3 py-1 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      onOpenBet({
                        play_service_id: String(row.play_service_id),
                        game_uuid: String(row.game_uuid),
                        bet_id: String(row.bet_id),
                      })
                    }
                    title="Open this bet in Bet Inspection"
                    className="inline-flex items-center gap-1 border border-hairline px-1.5 py-[2px] font-condensed text-[10px] uppercase tracking-wider text-text-dim hover:border-accent/40 hover:text-accent"
                  >
                    <Telescope size={11} /> inspect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > RENDER_CAP && (
        <p className="border-t border-hairline px-4 py-2 text-[11.5px] text-warn">
          Showing the first {RENDER_CAP} of {rows.length} rows
          {find.trim() ? ' matching your filter' : ''}. The CSV export contains all of them.
        </p>
      )}
      {rows.length === 0 && (
        <p className="px-4 py-6 text-center text-[12.5px] text-text-dim">
          No row in the feed matches your filter.
        </p>
      )}
    </>
  );
}

/**
 * game-core returns `dt` as ClickHouse's `YYYY-MM-DD HH:mm:ss` with no zone
 * marker; it is UTC (§4.1). Parse it as such rather than letting the browser
 * read it in local time — that is the exact skew this console exists to avoid.
 */
function FeedTime({ value, tz }: { value?: string; tz: TimeZoneMode }) {
  const date = parseClickhouseTime(value);
  if (!date) return <span className="text-text-faint">—</span>;
  return <Timestamp date={date} raw={value ?? null} mode={tz} withDate className="text-[11.5px]" />;
}

/** Could not fetch ≠ the partner has nothing. These must never look alike. */
function Unavailable({ result }: { result: ReconciliationResult }) {
  return (
    <section className="border-l-2 border-l-crit bg-crit-bg px-4 py-3">
      <div className="mb-1 flex items-center gap-2">
        <AlertCircle size={15} className="shrink-0 text-crit" />
        <span className="font-condensed text-[13px] font-semibold uppercase tracking-wider text-crit">
          The feed could not be fetched
        </span>
      </div>
      <p className="text-[12.5px] leading-relaxed text-text">
        This is <strong>not</strong> the same as the partner having no transactions in this window —
        we simply could not read it. A partner calling the same endpoint right now may well be
        getting an error too.
      </p>
      {result.feed.error && (
        <p className="mt-1 font-mono text-[11px] text-crit">{result.feed.error}</p>
      )}
    </section>
  );
}

function Empty() {
  return (
    <div className="flex min-h-[28vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Scale size={20} className="text-text-faint" />
      <p className="max-w-[520px] text-[13px] leading-relaxed text-text-dim">
        The feed was fetched successfully and is empty for this window — a partner reconciling this
        period would receive no rows.
      </p>
      <p className="max-w-[520px] text-[12px] leading-relaxed text-text-faint">
        Remember the feed only carries <strong>settled</strong> bets (a stake with a win or a
        rollback), and is built from a copy that runs about an hour behind, so a window of very
        recent or still-open bets is legitimately empty.
      </p>
    </div>
  );
}

function Cell({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="eyebrow mb-0.5">{label}</div>
      <div className="flex h-[22px] items-center">{children}</div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={cn(
        'px-3 py-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-faint',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Scanning() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-4">
      <div className="lens-scan relative h-px w-[220px] overflow-hidden bg-hairline" />
      <p className="font-condensed text-[11px] uppercase tracking-[0.2em] text-text-dim">
        fetching the partner reconciliation feed
      </p>
    </div>
  );
}

function Idle() {
  return (
    <div className="instrument-bg flex min-h-[40vh] flex-col items-center justify-center gap-3 px-6 text-center">
      <Scale size={22} className="text-text-faint" />
      <p className="max-w-[520px] text-[13px] leading-relaxed text-text-dim">
        See exactly what a partner receives when they pull our{' '}
        <Explain term="partner_feed">reconciliation feed</Explain> for a period, and export it. This
        is the screen for “your reconciliation data does not match ours” — it puts the file itself
        in front of you.
      </p>
      <p className="max-w-[520px] text-[12px] leading-relaxed text-text-faint">
        Pick a project and a window to begin. Narrow windows are both faster and more trustworthy —
        the feed returns at most 1000 bets, for partners and for us alike.
      </p>
    </div>
  );
}

function Failure({ error }: { error: Error }) {
  const status = error instanceof ApiError ? error.status : 0;
  return (
    <div className="flex min-h-[30vh] items-start justify-center px-6 pt-12">
      <div className="flex max-w-[520px] gap-3 border border-crit/40 bg-crit-bg p-4">
        <AlertCircle size={16} className="mt-[2px] shrink-0 text-crit" />
        <div>
          <div className="mb-1 font-condensed text-[11px] font-semibold uppercase tracking-wider text-crit">
            {status === 403 ? 'Not permitted' : 'Could not load the feed'}
          </div>
          <p className="text-[12.5px] leading-relaxed text-text">{error.message}</p>
        </div>
      </div>
    </div>
  );
}
